import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { formatCLP, statusBadge, formatTracking } from "../lib/format";
import { StatusTimeline } from "../components/StatusTimeline";
import { StarRating } from "../components/StarRating";
import { LiveTracking, ServiceChat, DispatchGuide } from "../components/ServicePanel";
import { ServiceEvidence, AssignedResources } from "../components/ServiceEvidence";
import { MapPin, MapPinLine, Truck, ArrowLeft, CircleNotch, CheckCircle, Star, Copy } from "@phosphor-icons/react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";

const NEXT_STATUS = {
  ADJUDICADO: "TRANSPORTISTA_EN_CAMINO",
  TRANSPORTISTA_EN_CAMINO: "EN_ORIGEN",
  EN_ORIGEN: "CARGA_RECOGIDA",
  CARGA_RECOGIDA: "EN_TRASLADO",
  EN_TRASLADO: "ENTREGADO",
};
const NEXT_LABEL = {
  ADJUDICADO: "Iniciar viaje (voy en camino)",
  TRANSPORTISTA_EN_CAMINO: "Llegué al origen",
  EN_ORIGEN: "Carga recogida",
  CARGA_RECOGIDA: "Iniciar traslado",
  EN_TRASLADO: "Marcar como entregado",
};

export default function ServiceDetail() {
  const { id } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [svc, setSvc] = useState(null);
  const [busy, setBusy] = useState(false);
  const [rateOpen, setRateOpen] = useState(false);
  const [rating, setRating] = useState({ punctuality: 5, cargoCare: 5, communication: 5, overallRating: 5, comment: "" });
  const [rateClientOpen, setRateClientOpen] = useState(false);
  const [clientRating, setClientRating] = useState({ communication: 5, paymentReliability: 5, overallRating: 5, comment: "" });
  const [config, setConfig] = useState({});

  const load = () => api.get(`/services/${id}`).then((r) => setSvc(r.data)).catch(() => toast.error("No se pudo cargar"));
  useEffect(() => { load(); }, [id]);
  useEffect(() => { api.get("/meta/config").then((r) => setConfig(r.data)).catch(() => {}); }, []);

  const isCarrier = user.role === "CARRIER";
  const isClient = user.role === "CLIENT";

  const advance = async (status) => {
    setBusy(true);
    try { await api.patch(`/services/${id}/status`, { status }); toast.success("Estado actualizado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const submitRating = async () => {
    setBusy(true);
    try { await api.post(`/services/${id}/rate`, rating); toast.success("¡Gracias por tu calificación!"); setRateOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  const submitClientRating = async () => {
    setBusy(true);
    try { await api.post(`/services/${id}/rate-client`, clientRating); toast.success("¡Gracias por calificar al cliente!"); setRateClientOpen(false); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    setBusy(false);
  };

  if (!svc) return <div className="flex justify-center py-24"><CircleNotch size={36} className="animate-spin text-[#FF5A36]" /></div>;

  const f = svc.freight || {};
  const badge = statusBadge(svc.status);
  const next = NEXT_STATUS[svc.status];

  return (
    <div className="w-full px-4 sm:px-6 lg:px-8 py-6" data-testid="service-detail">
      <button onClick={() => navigate(isCarrier ? "/app/carrier" : "/app/client")} className="flex items-center gap-1 text-sm font-semibold text-zinc-600 hover:text-[#0F1115] mb-4">
        <ArrowLeft size={16} weight="bold" /> Volver
      </button>

      <div className="lg:grid lg:grid-cols-3 lg:gap-4 lg:items-start">
        {/* Columna principal */}
        <div className="lg:col-span-2 space-y-4">
          <div className="rounded-2xl border border-zinc-200 bg-white p-4">
            <div className="flex items-start justify-between">
              <div>
                <span className="text-xs font-bold uppercase tracking-widest text-[#FF5A36]">{f.category}</span>
                <h1 className="font-display text-xl font-bold tracking-tight mt-0.5">{f.origin?.city} → {f.destination?.city}</h1>
                {svc.trackingNumber && (
                  <button data-testid="tracking-number"
                    onClick={() => { navigator.clipboard?.writeText(svc.trackingNumber); toast.success("N° de traslado copiado"); }}
                    className="mt-1 inline-flex items-center gap-1.5 text-xs font-semibold text-zinc-500 hover:text-[#0F1115] transition-colors"
                    title="Copiar número de traslado">
                    N° Traslado <span className="font-mono font-bold tracking-wide text-zinc-700">{formatTracking(svc.trackingNumber)}</span>
                    <Copy size={13} />
                  </button>
                )}
              </div>
              <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${badge.color}`}>{badge.label}</span>
            </div>
            <div className="grid sm:grid-cols-2 gap-3 mt-3 text-sm">
              <div className="flex items-start gap-2"><MapPin size={18} className="text-zinc-400 mt-0.5" /><div><div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Origen</div>{f.origin?.address}</div></div>
              <div className="flex items-start gap-2"><MapPinLine size={18} className="text-zinc-400 mt-0.5" /><div><div className="text-xs uppercase tracking-widest text-zinc-400 font-bold">Destino</div>{f.destination?.address}</div></div>
            </div>
            <div className="border-t border-zinc-100 mt-3 pt-3 flex items-center justify-between">
              <div className="flex items-center gap-2 text-sm">
                <Truck size={18} className="text-[#FF5A36]" weight="bold" />
                {isCarrier ? (
                  <span className="font-medium flex items-center gap-1.5" data-testid="client-reputation">
                    {svc.client?.name}
                    {svc.client?.ratingCount > 0
                      ? <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Star size={12} weight="fill" className="text-[#FF5A36]" />{svc.client.rating} ({svc.client.ratingCount})</span>
                      : <span className="text-xs text-zinc-400">Cliente sin calificaciones</span>}
                  </span>
                ) : (
                  <>
                    <span className="font-medium">{svc.carrier?.name}</span>
                    {svc.carrier?.rating ? <span className="text-xs text-zinc-500 flex items-center gap-0.5"><Star size={12} weight="fill" className="text-[#FF5A36]" />{svc.carrier.rating}</span> : null}
                  </>
                )}
              </div>
              <span className="font-display text-xl font-black">{formatCLP(svc.agreedPrice)}</span>
            </div>
            <AssignedResources svc={svc} />
          </div>

          <div className="rounded-2xl border border-zinc-200 bg-white p-5">
            <h2 className="font-semibold mb-4">Seguimiento de la carga</h2>
            <StatusTimeline status={svc.status} history={svc.statusHistory} />
          </div>

          {svc.status !== "CANCELADO" && (
            <ServiceEvidence serviceId={id} svc={svc} isCarrier={isCarrier} onSaved={load} />
          )}

          {svc.status !== "COMPLETADO" && svc.status !== "CANCELADO" && (
            <div className="space-y-3">
              {isCarrier && next && (
                <button onClick={() => advance(next)} disabled={busy} data-testid="advance-status-btn"
                  className="w-full py-4 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
                  {busy ? <CircleNotch size={20} className="animate-spin" /> : NEXT_LABEL[svc.status]}
                </button>
              )}
              {isClient && svc.status === "ENTREGADO" && (
                <button onClick={() => advance("COMPLETADO")} disabled={busy} data-testid="confirm-received-btn"
                  className="w-full py-4 rounded-xl bg-emerald-600 text-white font-bold flex items-center justify-center gap-2 hover:bg-emerald-700 transition-colors active:scale-95 disabled:opacity-60">
                  {busy ? <CircleNotch size={20} className="animate-spin" /> : <><CheckCircle size={20} weight="bold" /> Confirmar recepción</>}
                </button>
              )}
              {isClient && !["ENTREGADO"].includes(svc.status) && (
                <p className="text-center text-sm text-zinc-500">El transportista está gestionando tu servicio. Recibirás notificaciones en cada avance.</p>
              )}
              {isCarrier && !next && <p className="text-center text-sm text-zinc-500">Esperando que el cliente confirme la recepción.</p>}
            </div>
          )}

          {svc.status === "COMPLETADO" && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-6 text-center">
              <CheckCircle size={40} weight="fill" className="text-emerald-600 mx-auto mb-2" />
              <div className="font-semibold text-emerald-800">Servicio completado</div>
              {isClient && !svc.rated && (
                <button onClick={() => setRateOpen(true)} data-testid="open-rating-btn"
                  className="mt-4 px-6 py-3 rounded-full bg-[#FF5A36] text-white font-bold inline-flex items-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95">
                  <Star size={18} weight="fill" /> Calificar transportista
                </button>
              )}
              {isClient && svc.rated && <p className="mt-2 text-sm text-emerald-700">¡Gracias por calificar!</p>}
              {isCarrier && !svc.carrierRated && (
                <button onClick={() => setRateClientOpen(true)} data-testid="open-rate-client-btn"
                  className="mt-4 px-6 py-3 rounded-full bg-[#FF5A36] text-white font-bold inline-flex items-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95">
                  <Star size={18} weight="fill" /> Calificar cliente
                </button>
              )}
              {isCarrier && svc.carrierRated && <p className="mt-2 text-sm text-emerald-700">¡Gracias por calificar al cliente!</p>}
            </div>
          )}
        </div>

        {/* Columna lateral: documentación y chat */}
        {svc.status !== "CANCELADO" && (
          <div className="mt-4 lg:mt-0 [&>*]:mb-0 space-y-4">
            <DispatchGuide serviceId={id} svc={svc} isClient={isClient} config={config} onSaved={load} />
            {["ADJUDICADO", "TRANSPORTISTA_EN_CAMINO", "EN_ORIGEN", "CARGA_RECOGIDA", "EN_TRASLADO", "ENTREGADO"].includes(svc.status) && (
              <LiveTracking serviceId={id} active={svc.status !== "ENTREGADO"} isCarrier={isCarrier} />
            )}
            <ServiceChat serviceId={id} userId={user.id} />
          </div>
        )}
      </div>

      <Dialog open={rateOpen} onOpenChange={setRateOpen}>
        <DialogContent data-testid="rating-dialog">
          <DialogHeader><DialogTitle>Califica a {svc.carrier?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {[
              ["punctuality", "Puntualidad"],
              ["cargoCare", "Cuidado de la carga"],
              ["communication", "Comunicación"],
              ["overallRating", "Evaluación general"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm font-medium">{label}</span>
                <StarRating value={rating[key]} size={26} onChange={(v) => setRating({ ...rating, [key]: v })} testid={`rate-${key}`} />
              </div>
            ))}
            <textarea data-testid="rating-comment" rows={3} value={rating.comment} onChange={(e) => setRating({ ...rating, comment: e.target.value })}
              placeholder="Comentario (opcional)" className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36] resize-none" />
            <button onClick={submitRating} disabled={busy} data-testid="submit-rating"
              className="w-full py-3.5 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
              {busy ? <CircleNotch size={18} className="animate-spin" /> : "Enviar calificación"}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={rateClientOpen} onOpenChange={setRateClientOpen}>
        <DialogContent data-testid="rate-client-dialog">
          <DialogHeader><DialogTitle>Califica a {svc.client?.name}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {[
              ["communication", "Comunicación"],
              ["paymentReliability", "Formalidad de pago"],
              ["overallRating", "Evaluación general"],
            ].map(([key, label]) => (
              <div key={key} className="flex items-center justify-between">
                <span className="text-sm font-medium">{label}</span>
                <StarRating value={clientRating[key]} size={26} onChange={(v) => setClientRating({ ...clientRating, [key]: v })} testid={`rate-client-${key}`} />
              </div>
            ))}
            <textarea data-testid="client-rating-comment" rows={3} value={clientRating.comment} onChange={(e) => setClientRating({ ...clientRating, comment: e.target.value })}
              placeholder="Comentario (opcional)" className="w-full px-4 py-3 rounded-xl border border-zinc-300 focus:outline-none focus:ring-2 focus:ring-[#FF5A36] resize-none" />
            <button onClick={submitClientRating} disabled={busy} data-testid="submit-client-rating"
              className="w-full py-3.5 rounded-xl bg-[#FF5A36] text-white font-bold flex items-center justify-center gap-2 hover:bg-[#E04B2A] transition-colors active:scale-95 disabled:opacity-60">
              {busy ? <CircleNotch size={18} className="animate-spin" /> : "Enviar calificación"}
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
